import { Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent } from 'react'
import { NumericFormat } from 'react-number-format'
import gaiaTheme from '../../../root-components/theme'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import {
  getGaiaReportBodyTextSx,
  getGaiaReportSectionShellSx,
} from '../../public-reports/components/gaia-report-web-page-pdf-styles'
import type { IColorPalatte, IProjectReportProjectDetails, IProjectReportScopeTotals } from '../shared-services/project-reports.model'
import { useReportContentOverride } from '../../public-reports/shared-services/gaia-report-content'

const ScopeCard: FunctionComponent<{ label: string; value: number; color: string; pdfExport: boolean }> = ({ label, value, color, pdfExport }) => (
  <Box sx={{
    backgroundColor: color,
    borderRadius: 2,
    p: pdfExport ? 1.5 : 2,
    textAlign: 'center',
    flex: 1,
  }}>
    <Typography variant='caption' sx={{ color: 'white', fontWeight: 600, display: 'block' }}>{label}</Typography>
    <Typography variant={pdfExport ? 'h6' : 'h5'} sx={{ color: 'white', fontWeight: 700 }}>
      <NumericFormat value={value.toFixed(1)} displayType='text' thousandSeparator />
    </Typography>
    <Typography variant='caption' sx={{ color: 'white', opacity: 0.85 }}>tCO₂e</Typography>
  </Box>
)

export const ProjectReportWebPageOverview: FunctionComponent<{
  colorPalatte: IColorPalatte
  projectDetails: IProjectReportProjectDetails
  scopeTotals: IProjectReportScopeTotals
  projectLabel: string
}> = ({ colorPalatte, projectDetails, scopeTotals, projectLabel }) => {
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'))
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'))
  const pdfExport = usePdfExportMode()

  const heading = useReportContentOverride('overview.heading')
  const totalsHeading = useReportContentOverride('overview.totalsHeading')

  const formatDate = (date: string | null | undefined) => {
    if (!date) return null
    try {
      return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    } catch {
      return null
    }
  }

  return (
    <Stack sx={{
      ...getGaiaReportSectionShellSx({ backgroundColor: 'white', pdfExport }),
      ...(pdfExport ? {} : { mt: -5, position: 'relative', zIndex: 1 }),
    }}>
      <Typography variant={isMobileModeSm ? 'h5' : 'h4'} sx={{ color: colorPalatte.dark, fontWeight: 700, mb: 2 }}>
        {heading ?? `${projectLabel} Overview`}
      </Typography>
      {projectDetails.description && (
        <Typography sx={{ ...getGaiaReportBodyTextSx({ color: 'text.secondary', isMobileModeMd, isMobileModeSm, pdfExport }), mb: 3 }}>
          {projectDetails.description}
        </Typography>
      )}
      <Grid container spacing={pdfExport ? 1.5 : 2} sx={{ mb: 3 }}>
        {projectDetails.projectReference && (
          <Grid item xs={12} sm={6}>
            <Typography variant='caption' color='text.secondary'>Reference</Typography>
            <Typography variant='body1' fontWeight={600}>{projectDetails.projectReference}</Typography>
          </Grid>
        )}
        {projectDetails.startDate && (
          <Grid item xs={6} sm={3}>
            <Typography variant='caption' color='text.secondary'>Start</Typography>
            <Typography variant='body1' fontWeight={600}>{formatDate(projectDetails.startDate)}</Typography>
          </Grid>
        )}
        {projectDetails.endDate && (
          <Grid item xs={6} sm={3}>
            <Typography variant='caption' color='text.secondary'>End</Typography>
            <Typography variant='body1' fontWeight={600}>{formatDate(projectDetails.endDate)}</Typography>
          </Grid>
        )}
      </Grid>
      <Typography variant='h6' sx={{ color: colorPalatte.dark, fontWeight: 700, mb: 1.5 }}>
        {totalsHeading ?? 'Total Emissions'}
      </Typography>
      <Stack direction='row' spacing={pdfExport ? 1 : 1.5} flexWrap='wrap' useFlexGap>
        <ScopeCard label='Scope 1' value={scopeTotals.scope1} color={colorPalatte.veryDark} pdfExport={pdfExport} />
        <ScopeCard label='Scope 2' value={scopeTotals.scope2} color={colorPalatte.dark} pdfExport={pdfExport} />
        <ScopeCard label='Scope 3' value={scopeTotals.scope3} color={colorPalatte.main} pdfExport={pdfExport} />
        <ScopeCard label='Total' value={scopeTotals.total} color={colorPalatte.light} pdfExport={pdfExport} />
      </Stack>
    </Stack>
  )
}
